import * as txpConstants from '../../helpers/TxpConstants';
import createEntityIllustration from './createEntityIllustration';
import { createCalendarTxpDetailsIcons, createMailTxpDetailsIcons } from './createDetailsIcons';
import { ControlIcons } from 'owa-control-icons';
import { isDefaultColor } from 'owa-color-utils/lib/colorUtils';
import isDefaultTheme from './isDefaultTheme';
import { getIsDarkTheme } from 'owa-fabric-theme';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import TxpImages from 'txp-data/lib/schema/configSchema/TxpImages';

/**
 * Return the svg assets to use in mail and calendar peek txp cards, when dark mode is set
 */
const getDarkThemeImages = () => ({
    flight: txpConstants.FLIGHT_ILLUSTRATION_DARK_THEME,
    lodging: txpConstants.LODGING_ILLUSTRATION_DARK_THEME,
    parcelDelivery: txpConstants.PARCEL_DELIVERY_ILLUSTRATION_DARK_THEME,
    rentalCar: txpConstants.RENTAL_CAR_ILLUSTRATION_DARK_THEME,
    event: txpConstants.EVENT_ILLUSTRATION_DARK_THEME,
    dining: txpConstants.DINING_ILLUSTRATION_DARK_THEME,
    invoice: txpConstants.INVOICE_ILLUSTRATION_DARK_THEME,
    serviceReservation: txpConstants.SERVICE_RESERVATION_ILLUSTRATION_DARK_THEME,
});

/**
 * Return the svg assets to use in mail and calendar peek txp cards, when there is a non-default theme or calendar/category color set
 */
const getMonoChromeImages = () => ({
    flight: txpConstants.FLIGHT_ILLUSTRATION,
    lodging: txpConstants.LODGING_ILLUSTRATION,
    parcelDelivery: txpConstants.PARCEL_DELIVERY_ILLUSTRATION,
    rentalCar: txpConstants.RENTAL_CAR_ILLUSTRATION,
    event: txpConstants.EVENT_ILLUSTRATION,
    dining: txpConstants.DINING_ILLUSTRATION,
    invoice: txpConstants.INVOICE_ILLUSTRATION,
    serviceReservation: txpConstants.SERVICE_RESERVATION_ILLUSTRATION,
});

/**
 * Return the svg assets to use in txp read form, when there is a non-default calendar/category color set
 */
const getMonoChromeWideImages = () => ({
    flight: txpConstants.FLIGHT_ILLUSTRATION_WIDE,
    lodging: txpConstants.LODGING_ILLUSTRATION_WIDE,
    parcelDelivery: txpConstants.PARCEL_DELIVERY_ILLUSTRATION_WIDE,
    rentalCar: txpConstants.RENTAL_CAR_ILLUSTRATION_WIDE,
    event: txpConstants.EVENT_ILLUSTRATION_WIDE,
    dining: txpConstants.DINING_ILLUSTRATION_WIDE,
    invoice: txpConstants.INVOICE_ILLUSTRATION_WIDE,
    serviceReservation: txpConstants.SERVICE_RESERVATION_ILLUSTRATION_WIDE,
});

/**
 * Return the svg assets to use in mail and calendar peek txp cards, when the default theme or calendar/category color is set
 */
const getTriColorImages = () => ({
    flight: txpConstants.FLIGHT_ILLUSTRATION_TRICOLOR,
    lodging: txpConstants.LODGING_ILLUSTRATION_TRICOLOR,
    parcelDelivery: txpConstants.PARCEL_DELIVERY_ILLUSTRATION_TRICOLOR,
    rentalCar: txpConstants.RENTAL_CAR_ILLUSTRATION_TRICOLOR,
    event: txpConstants.EVENT_ILLUSTRATION_TRICOLOR,
    dining: txpConstants.DINING_ILLUSTRATION_TRICOLOR,
    invoice: txpConstants.INVOICE_ILLUSTRATION_TRICOLOR,
    serviceReservation: txpConstants.SERVICE_RESERVATION_ILLUSTRATION_TRICOLOR,
});

/**
 * Return the svg assets to use in txp read form, when the calendar/category color is set to default
 */
const getTriColorWideImages = () => ({
    flight: txpConstants.FLIGHT_ILLUSTRATION_WIDE_TRICOLOR,
    lodging: txpConstants.LODGING_ILLUSTRATION_WIDE_TRICOLOR,
    parcelDelivery: txpConstants.PARCEL_DELIVERY_ILLUSTRATION_WIDE_TRICOLOR,
    rentalCar: txpConstants.RENTAL_CAR_ILLUSTRATION_WIDE_TRICOLOR,
    event: txpConstants.EVENT_ILLUSTRATION_WIDE_TRICOLOR,
    dining: txpConstants.DINING_ILLUSTRATION_WIDE_TRICOLOR,
    invoice: txpConstants.INVOICE_ILLUSTRATION_WIDE_TRICOLOR,
    serviceReservation: txpConstants.SERVICE_RESERVATION_ILLUSTRATION_WIDE_TRICOLOR,
});

/**
 * Return the entity specific assets to use in mail txp cards depending on the theme set
 */
export function populateMailImages(): TxpImages {
    const {
        flight,
        lodging,
        parcelDelivery,
        rentalCar,
        event,
        dining,
        invoice,
        serviceReservation,
    } =
        (getIsDarkTheme() && getDarkThemeImages()) ||
        (isDefaultTheme() && getTriColorImages()) ||
        getMonoChromeImages();

    return {
        flight: createEntityIllustration(flight),
        lodging: createEntityIllustration(lodging),
        parcelDelivery: createEntityIllustration(parcelDelivery),
        rentalCar: createEntityIllustration(rentalCar),
        event: createEntityIllustration(event),
        dining: createEntityIllustration(dining),
        invoice: createEntityIllustration(invoice),
        detailsIcons: createMailTxpDetailsIcons(),
        serviceReservation: createEntityIllustration(serviceReservation),
    };
}

/**
 * Return the entity specific assets to use in calendar peek txp cards depending on the calendar/category color and dark mode
 */
export function populateCalendarPeekImages(colorScheme?: ColorScheme): TxpImages {
    const {
        flight,
        lodging,
        parcelDelivery,
        rentalCar,
        event,
        dining,
        invoice,
        serviceReservation,
    } =
        (getIsDarkTheme() && getDarkThemeImages()) ||
        (colorScheme && isDefaultColor(colorScheme) && getTriColorImages()) ||
        getMonoChromeImages();

    return {
        flight: createEntityIllustration(flight, ControlIcons.Airplane),
        lodging: createEntityIllustration(lodging, ControlIcons.Hotel),
        parcelDelivery: createEntityIllustration(parcelDelivery, ControlIcons.GiftCard),
        rentalCar: createEntityIllustration(rentalCar, ControlIcons.Car),
        event: createEntityIllustration(event, ControlIcons.Ticket),
        dining: createEntityIllustration(dining, ControlIcons.EatDrink),
        invoice: createEntityIllustration(invoice, ControlIcons.PaymentCard),
        detailsIcons: createCalendarTxpDetailsIcons(),
        serviceReservation: createEntityIllustration(serviceReservation, ControlIcons.Stopwatch),
    };
}

/**
 * Return the entity specific assets to use in txp read form depending on the calendar/category color and dark mode
 */
export function populateCalendarFormImages(colorScheme?: ColorScheme): TxpImages {
    const {
        flight,
        lodging,
        parcelDelivery,
        rentalCar,
        event,
        dining,
        invoice,
        serviceReservation,
    } =
        (!getIsDarkTheme() &&
            colorScheme &&
            isDefaultColor(colorScheme) &&
            getTriColorWideImages()) ||
        getMonoChromeWideImages();

    return {
        flight: createEntityIllustration(flight, ControlIcons.Airplane),
        lodging: createEntityIllustration(lodging, ControlIcons.Hotel),
        parcelDelivery: createEntityIllustration(parcelDelivery, ControlIcons.GiftCard),
        rentalCar: createEntityIllustration(rentalCar, ControlIcons.Car),
        event: createEntityIllustration(event, ControlIcons.Ticket),
        dining: createEntityIllustration(dining, ControlIcons.EatDrink),
        invoice: createEntityIllustration(invoice, ControlIcons.PaymentCard),
        detailsIcons: createCalendarTxpDetailsIcons(),
        serviceReservation: createEntityIllustration(serviceReservation, ControlIcons.Stopwatch),
    };
}
